import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import fileIcon from '../assets/Paperclip.png';
import pingSound from '../assets/audio/ping.mp3';

// ✅ Create socket instance globally
const socket = io(process.env.REACT_APP_SOCKET_URL, {
  withCredentials: true,
});


//socket closed

const FloatingChatWindow = ({
  chat,
  index = 0,
  onClose = () => {},
  isMinimized = false,
  toggleMinimized = () => {},
  onMessageSent = () => {},
}) => {
  const [messages, setMessages] = useState(chat.messages || []);
  const [reply, setReply] = useState('');
  const [file, setFile] = useState(null);
  const [otherTyping, setOtherTyping] = useState(false);

  const fileInputRef = useRef();
  const messagesEndRef = useRef();
  const soundRef = useRef();

  const accessToken = localStorage.getItem('accessToken');
  let currentUserId = '';

  try {
    currentUserId = JSON.parse(atob(accessToken.split('.')[1])).id;
  } catch {}

  // ✅ Join socket room on mount
  useEffect(() => {
    socket.emit('join', chat.id);
    console.log(`📡 Joined chat room: chat-${chat.id}`);

    const handleIncoming = ({ chatId, message }) => {
      if (chatId !== chat.id) return;

      setMessages((prev) => [...prev, message]);

      if (message.senderId !== currentUserId) {
        soundRef.current?.play().catch(err => console.warn('🔇 Audio blocked:', err));
      }

      onMessageSent(chat.id, message);
    };

    const handleTyping = ({ chatId }) => {
      if (chatId === chat.id) {
        setOtherTyping(true);
        setTimeout(() => setOtherTyping(false), 2000);
      }
    };

    socket.on('receive-message', handleIncoming);
    socket.on('typing', handleTyping);

    return () => {
      socket.off('receive-message', handleIncoming);
      socket.off('typing', handleTyping);
    };
  }, [chat.id]);

  // 🔽 Auto-scroll on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 📤 Send message via API only
  const sendMessage = async () => {
    if (!reply.trim() && !file) return;

    const fd = new FormData();
    fd.append('chatId', chat.id);
    fd.append('content', reply);
    fd.append('type', file ? 'file' : 'text');
    if (file) fd.append('file', file);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/support-chat/message`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const msg = res.data.message;
      console.log('✅ Message sent via API:', msg);

      // ⛔ No need to manually push to state — socket will handle it
      setReply('');
      setFile(null);
    } catch (err) {
      console.error('❌ Failed to send message:', err);
    }
  };

  const onTyping = (e) => {
    setReply(e.target.value);
    socket.emit('typing', { chatId: chat.id });
  };

  const onFileSelect = (e) => setFile(e.target.files[0]);
  const isImage = (url) => /\.(jpe?g|png|gif|svg|webp)$/i.test(url);

  return (
    <div style={styles.chatWrapper(isMinimized, index)}>
      <audio ref={soundRef} src={pingSound} preload="auto" />

      <div style={styles.header}>
        <strong>{chat.user.fullName} ↔ Admin</strong>
        <div>
          <button onClick={() => toggleMinimized(chat.id)} style={styles.btn}>
            {isMinimized ? '🔼' : '🔽'}
          </button>
          <button onClick={() => onClose(chat.id)} style={styles.btn}>❌</button>
        </div>
      </div>

      {!isMinimized && (
        <div style={styles.body}>
          <div style={styles.messages}>
            <div style={{ height: '20px' }} />
            {messages.map((msg, i) => {
              const fromMe = msg.senderId === currentUserId;
              const name = msg.fullName || (fromMe ? 'You' : chat.user.fullName);
              const content = msg.fileUrl || msg.content;
              const displayImage = isImage(content);

              return (
              <div
    key={i}
    style={{
      display: 'flex',
      justifyContent: fromMe ? 'flex-end' : 'flex-start',
    }}
  >
    <div style={styles.message(fromMe)}>
      <div style={{ fontWeight: 'bold' }}>{name}</div>
      {displayImage ? (
        <img src={content} alt="upload" style={styles.image} />
      ) : msg.type === 'file' ? (
        <a href={content} target="_blank" rel="noreferrer">📎 Download</a>
      ) : (
        <span>{content}</span>
      )}
      <div style={styles.meta}>
        {new Date(msg.timestamp).toLocaleTimeString()} {fromMe ? '✓✓' : ''}
      </div>
    </div>
  </div>
              );
            })}
            {otherTyping && <div style={styles.typing}>{chat.user.fullName} is typing...</div>}
            <div ref={messagesEndRef} />
          </div>

          <div style={styles.inputArea}>
            <input
              value={reply}
              onChange={onTyping}
              placeholder="Type a message..."
              style={styles.input}
            />
            <img src={fileIcon} style={styles.uploadIcon} onClick={() => fileInputRef.current.click()} />
            <input type="file" ref={fileInputRef} onChange={onFileSelect} style={{ display: 'none' }} />
            <button onClick={sendMessage} style={styles.sendBtn}>Send</button>
          </div>
        </div>
      )}
    </div>
  );
};



const styles = {
  chatWrapper: (isMinimized, index) => ({
    position: 'fixed',
    bottom: isMinimized ? '0' : '20px',
    right: `${20 + index * 340}px`,
    width: '320px',
    height: isMinimized ? '40px' : '450px',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '10px 10px 0 0',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 9999,
    boxShadow: '0 0 10px rgba(0,0,0,0.1)',
  }),
  header: {
    padding: '10px',
    backgroundColor: '#f7f7f7',
    borderBottom: '1px solid #ddd',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 'bold',
  },
  btn: {
    marginLeft: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    background: 'none',
    border: 'none',
  },
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  messages: {
    flex: 1,
    padding: '10px',
    overflowY: 'auto',
  },
  message: (fromMe) => ({
    alignSelf: fromMe ? 'flex-end' : 'flex-start',
    backgroundColor: fromMe ? '#dcf8c6' : '#f1f0f0',
    borderRadius: '8px',
    padding: '8px',
    margin: '8px 0',
    maxWidth: '85%',
  }),
  inputArea: {
    padding: '10px',
    display: 'flex',
    borderTop: '1px solid #ddd',
    alignItems: 'center',
    gap: '8px',
  },
  input: {
    flex: 1,
    padding: '8px',
    borderRadius: '5px',
    border: '1px solid #ccc',
  },
  sendBtn: {
    padding: '6px 12px',
    borderRadius: '5px',
    border: 'none',
    backgroundColor: '#007bff',
    color: '#fff',
    cursor: 'pointer',
  },
  uploadIcon: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  image: {
    maxWidth: '100%',
    maxHeight: '150px',
    borderRadius: '5px',
  },
  meta: {
    fontSize: '10px',
    color: '#777',
    textAlign: 'right',
    marginTop: '4px',
  },
  typing: {
    fontStyle: 'italic',
    fontSize: '12px',
    color: '#555',
    marginTop: '4px',
  },
};

export default FloatingChatWindow;
